<template>
    <table class="table table-striped table-hover">
        <tbody>
            <tr>
                <td>{{ l('PAYYO_PAYMENT_DETAILS.STATUS') }}</td>
                <td>
                    <PayyoSimpleStatusBadge :status="payment.status" />
                </td>
            </tr>
            <tr>
                <td>{{ l('PAYYO_PAYMENT_DETAILS.PAYMENT_ID') }}</td>
                <td>{{ payment.id }}</td>
            </tr>
            <tr>
                <td>{{ l('PAYYO_PAYMENT_DETAILS.CREATED_AT') }}</td>
                <td>{{ timeFilter(payment.created_at, false, true) }}</td>
            </tr>
            <tr>
                <td>{{ l('PAYYO_PAYMENT_DETAILS.UPDATED_AT') }}</td>
                <td>{{ timeFilter(payment.updated_at, false, true) }}</td>
            </tr>
            <tr>
                <td>{{ l('PAYYO_PAYMENT_DETAILS.AMOUNT') }}</td>
                <td>{{ currencyFilter(fromMinorUnit(payment.amount)) }}</td>
            </tr>
            <tr>
                <td>{{ l('PAYYO_PAYMENT_DETAILS.AMOUNT_CAPTURED') }}</td>
                <td>{{ currencyFilter(fromMinorUnit(payment.amount_captured)) }}</td>
            </tr>
            <tr :class="[canShowRefunds ? 'clickable' : '']"
                @click="canShowRefunds ? showHideRefunds() : () => {}">
                <td>{{ l('PAYYO_PAYMENT_DETAILS.AMOUNT_REFUNDED') }}</td>
                <td>
                    {{ currencyFilter(fromMinorUnit(payment.amount_refunded)) }}
                    <span v-if="canShowRefunds"
                          style="float: right;">
                        <i :class="toggleShowRefundsIcon"
                           class="fa-regular"></i>
                    </span>
                </td>
            </tr>
            <transition name="fade">
                <template v-if="showRefunds">
                    <tr class="payyo-refund-row mb-05">
                        <td colspan="2">
                            <p>{{ l('PAYYO_PAYMENT_DETAILS.REFUNDS') }}</p>
                            <div v-for="refund in refunds"
                                 :key="refund.id"
                                 class="card mt-05 pa-1">
                                <p>
                                    <span>{{ l('PAYYO_PAYMENT_DETAILS.STATUS') }}</span>
                                    <span>
                                        <PayyoRefundStatusBadge :status="refund.status" />
                                    </span>
                                </p>
                                <p>
                                    <span>{{ l('PAYYO_PAYMENT_DETAILS.REFUND_ID') }}</span>
                                    <span>{{ refund.id }}</span>
                                </p>
                                <p>
                                    <span>{{ l('PAYYO_PAYMENT_DETAILS.REFUND_AMOUNT') }}</span>
                                    <span>{{ currencyFilter(fromMinorUnit(refund.amount)) }}</span>
                                </p>
                                <p>
                                    <span>{{ l('PAYYO_PAYMENT_DETAILS.CREATED_AT') }}</span>
                                    <span>{{ timeFilter(refund.created_at, false, true) }}</span>
                                </p>
                                <p>
                                    <span>{{ l('PAYYO_PAYMENT_DETAILS.UPDATED_AT') }}</span>
                                    <span>{{ timeFilter(refund.created_at, false, true) }}</span>
                                </p>
                            </div>
                        </td>
                    </tr>
                </template>
            </transition>
            <tr>
                <td>{{ l('PAYYO_PAYMENT_DETAILS.AMOUNT_TIP') }}</td>
                <td>{{ currencyFilter(fromMinorUnit(payment.amount_tip)) }}</td>
            </tr>
            <tr>
                <td>{{ l('PAYYO_PAYMENT_DETAILS.DESCRIPTION') }}</td>
                <td>{{ payment.description }}</td>
            </tr>
            <tr>
                <td>{{ l('PAYYO_PAYMENT_DETAILS.DEVICE_ID') }}</td>
                <td>{{ payment.device_id }}</td>
            </tr>
            <tr>
                <td>{{ l('PAYYO_PAYMENT_DETAILS.MERCHANT_ID') }}</td>
                <td>{{ payment.merchant_id }}</td>
            </tr>
            <tr v-if="payment.metadata">
                <td>{{ l('PAYYO_PAYMENT_DETAILS.METADATA') }}</td>
                <td>{{ payment.metadata }}</td>
            </tr>
            <tr v-if="payment.last_error">
                <td>{{ l('PAYYO_PAYMENT_DETAILS.LAST_ERROR') }}</td>
                <td>
                    <p>{{ payment.last_error.code }}</p>
                    <p>{{ payment.last_error.decline_code }}</p>
                    <p>{{ payment.last_error.message }}</p>
                </td>
            </tr>
            <tr>
                <td>{{ l('PAYYO_PAYMENT_DETAILS.REFERENCE') }}</td>
                <td>{{ payment.reference }}</td>
            </tr>
            <tr v-if="payment.payment_method?.type">
                <td>{{ l('PAYYO_PAYMENT_DETAILS.TYPE') }}</td>
                <td>{{ payment.payment_method.type }}</td>
            </tr>
            <tr v-if="payment.payment_method?.card_present.brand">
                <td>{{ l('PAYYO_PAYMENT_DETAILS.SCHEME') }}</td>
                <td>{{ payment.payment_method.card_present.brand }}</td>
            </tr>
            <tr v-if="payment.payment_method?.card_present.last4">
                <td>{{ l('PAYYO_PAYMENT_DETAILS.LAST_4') }}</td>
                <td>************{{ payment.payment_method.card_present.last4 }}</td>
            </tr>
            <tr v-if="payment.payment_method?.card_present.read_method">
                <td>{{ l('PAYYO_PAYMENT_DETAILS.READ_METHOD') }}</td>
                <td>{{ payment.payment_method.card_present.read_method }}</td>
            </tr>
            <tr v-if="payment.payment_method?.card_present.receipt.authorization_code">
                <td>{{ l('PAYYO_PAYMENT_DETAILS.AUTH_CODE') }}</td>
                <td>{{ payment.payment_method.card_present.receipt.authorization_code }}</td>
            </tr>
            <tr v-if="payment.payment_method?.card_present.receipt.application_preferred_name">
                <td>{{ l('PAYYO_PAYMENT_DETAILS.APPLICATION_PREFERRED_NAME') }}</td>
                <td>{{ payment.payment_method.card_present.receipt.application_preferred_name }}</td>
            </tr>
            <tr v-if="payment.payment_method?.card_present.receipt.card_holder_verification_method">
                <td>{{ l('PAYYO_PAYMENT_DETAILS.VERIFICATION_METHOD') }}</td>
                <td>{{ payment.payment_method.card_present.receipt.card_holder_verification_method }}</td>
            </tr>
            <tr v-if="payment.payment_method?.card_present.receipt.dedicated_file_name">
                <td>{{ l('PAYYO_PAYMENT_DETAILS.AID') }}</td>
                <td>{{ payment.payment_method.card_present.receipt.dedicated_file_name }}</td>
            </tr>
        </tbody>
    </table>
</template>

<script>
import PayyoPaymentStatus from '@/lib/PaymentMethods/Payyo/Enums/PayyoPaymentStatus';
import PayyoRefundStatusBadge from '@/epos/vue/modals/Payment/PaymentMethods/Payyo/PayyoRefundStatusBadge';
import PayyoSimpleStatusBadge from '@/epos/vue/modals/Payment/PaymentMethods/Payyo/PayyoSimpleStatusBadge';
import { currencyFilter } from '@/vue/filters/currencyFilter';
import { fromMinorUnit } from '@/functions/math';
import { timeFilter } from '@/vue/filters/timeFilter';

export default {
    name: 'PayyoPaymentDetail',

    components: {
        PayyoRefundStatusBadge,
        PayyoSimpleStatusBadge,
    },

    props: {
        payment: {
            type: Object,
            default: null,
        },
        refunds: {
            type: Object,
            default: null,
        },
    },

    data() {
        return {
            loading: true,
            errorMessage: null,
            showRefunds: false,
        };
    },

    computed: {
        canShowRefunds() {
            return this.payment.amount_refunded > 0;
        },

        toggleShowRefundsIcon() {
            if (this.showRefunds === true) {
                // Lines are visible therefore the action is close.
                return 'fa-chevron-up';
            }
            // Lines are hidden therefore the action is open.
            return 'fa-chevron-down';
        },

        PayyoPaymentStatus() {
            return PayyoPaymentStatus;
        },
    },

    methods: {
        currencyFilter,
        fromMinorUnit,
        showHideRefunds() {
            if (this.refunds.length > 0) {
                this.showRefunds = !this.showRefunds;
            }
        },
        timeFilter,
    },
};
</script>

<style lang="less" scoped>
@import "../../../../../../less/variables.less";
.card > p {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
}
.card {
    border: 1px solid @btn-default-border;
    border-radius: @border-radius-base;
}
</style>
